import { useState, useEffect, useMemo } from 'react';
import { dashboardHandler, CustomDateParams, createCancelSource, isRequestCancelled } from '../DashboardHandler';
import { mapGameHistoryResponse } from '../utils/mappers';
import type { ChartDataPoint, PeriodComparison, DailyChallengeParticipation, CustomDateRange } from '../types';
import { getComparisonDays } from '../Components/DateRangePicker';

interface CachedData {
  data: ChartDataPoint[];
  totalGames: number;
  // Started games comparisons
  vsGamesComparison?: PeriodComparison;
  singleGamesComparison?: PeriodComparison;
  dailyChallengeComparison?: PeriodComparison;
  vsBotComparison?: PeriodComparison;
  // Completed games comparisons
  vsGamesCompletedComparison?: PeriodComparison;
  singleGamesCompletedComparison?: PeriodComparison;
  vsBotCompletedComparison?: PeriodComparison;
  // Daily challenge participation (unique users)
  dailyChallengeParticipation?: DailyChallengeParticipation;
}

interface UseGameHistoryReturn {
  data: ChartDataPoint[];
  totalGames: number;
  loading: boolean;
  error: string | null;
  // Started games comparisons
  vsGamesComparison?: PeriodComparison;
  singleGamesComparison?: PeriodComparison;
  dailyChallengeComparison?: PeriodComparison;
  vsBotComparison?: PeriodComparison;
  // Completed games comparisons
  vsGamesCompletedComparison?: PeriodComparison;
  singleGamesCompletedComparison?: PeriodComparison;
  vsBotCompletedComparison?: PeriodComparison;
  // Daily challenge participation (unique users)
  dailyChallengeParticipation?: DailyChallengeParticipation;
}

// Module-level cache shared across all hook instances
const globalCache: Record<string, CachedData> = {};

const getCacheKey = (period: string, customRange?: CustomDateRange): string => {
  if (period === 'custom' && customRange) {
    const compDays = getComparisonDays(customRange);
    return `custom:${customRange.startDate}:${customRange.endDate}:${compDays ?? 'auto'}`;
  }
  return period;
};

export const useGameHistory = (period: string, customRange?: CustomDateRange): UseGameHistoryReturn => {
  const [data, setData] = useState<ChartDataPoint[]>([]);
  const [totalGames, setTotalGames] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Started games comparisons
  const [vsGamesComparison, setVsGamesComparison] = useState<PeriodComparison | undefined>();
  const [singleGamesComparison, setSingleGamesComparison] = useState<PeriodComparison | undefined>();
  const [dailyChallengeComparison, setDailyChallengeComparison] = useState<PeriodComparison | undefined>();
  const [vsBotComparison, setVsBotComparison] = useState<PeriodComparison | undefined>();
  // Completed games comparisons
  const [vsGamesCompletedComparison, setVsGamesCompletedComparison] = useState<PeriodComparison | undefined>();
  const [singleGamesCompletedComparison, setSingleGamesCompletedComparison] = useState<PeriodComparison | undefined>();
  const [vsBotCompletedComparison, setVsBotCompletedComparison] = useState<PeriodComparison | undefined>();
  // Daily challenge participation (unique users)
  const [dailyChallengeParticipation, setDailyChallengeParticipation] = useState<DailyChallengeParticipation | undefined>();

  const cacheKey = useMemo(() => getCacheKey(period, customRange), [period, customRange]);

  useEffect(() => {
    // Don't fetch if custom mode but no range provided
    if (period === 'custom' && !customRange) return;

    // Check cache first
    if (globalCache[cacheKey]) {
      const cached = globalCache[cacheKey];
      setData(cached.data);
      setTotalGames(cached.totalGames);
      setVsGamesComparison(cached.vsGamesComparison);
      setSingleGamesComparison(cached.singleGamesComparison);
      setDailyChallengeComparison(cached.dailyChallengeComparison);
      setVsBotComparison(cached.vsBotComparison);
      setVsGamesCompletedComparison(cached.vsGamesCompletedComparison);
      setSingleGamesCompletedComparison(cached.singleGamesCompletedComparison);
      setVsBotCompletedComparison(cached.vsBotCompletedComparison);
      setDailyChallengeParticipation(cached.dailyChallengeParticipation);
      return;
    }

    const source = createCancelSource();
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Build custom date params if in custom mode
        let customDate: CustomDateParams | undefined;
        if (period === 'custom' && customRange) {
          customDate = {
            startDate: customRange.startDate,
            endDate: customRange.endDate,
            comparisonDays: getComparisonDays(customRange),
          };
        }

        const result = await dashboardHandler.getGameHistory(period, source.token, customDate);

        if (isMounted) {
          const mappedData = mapGameHistoryResponse(result);
          const total = result?.totalGames || 0;

          // Extract comparison data from API response
          const vsComparison = result?.vsGamesComparison ? {
            currentPeriodTotal: result.vsGamesComparison.currentPeriodTotal ?? 0,
            previousPeriodTotal: result.vsGamesComparison.previousPeriodTotal ?? 0,
            percentageChange: result.vsGamesComparison.percentageChange ?? 0,
          } : undefined;

          const singleComparison = result?.singleGamesComparison ? {
            currentPeriodTotal: result.singleGamesComparison.currentPeriodTotal ?? 0,
            previousPeriodTotal: result.singleGamesComparison.previousPeriodTotal ?? 0,
            percentageChange: result.singleGamesComparison.percentageChange ?? 0,
          } : undefined;

          const dailyComparison = result?.dailyChallengeComparison ? {
            currentPeriodTotal: result.dailyChallengeComparison.currentPeriodTotal ?? 0,
            previousPeriodTotal: result.dailyChallengeComparison.previousPeriodTotal ?? 0,
            percentageChange: result.dailyChallengeComparison.percentageChange ?? 0,
          } : undefined;

          // VS Bot comparison (unranked bot games)
          const resultAnyForVsBot = result as any;
          const vsBotComp = resultAnyForVsBot?.vsBotComparison ? {
            currentPeriodTotal: resultAnyForVsBot.vsBotComparison.currentPeriodTotal ?? 0,
            previousPeriodTotal: resultAnyForVsBot.vsBotComparison.previousPeriodTotal ?? 0,
            percentageChange: resultAnyForVsBot.vsBotComparison.percentageChange ?? 0,
          } : undefined;

          // Completed games comparisons (cast to any until API types are regenerated)
          const resultAny = result as any;
          const vsCompletedComparison = resultAny?.vsGamesCompletedComparison ? {
            currentPeriodTotal: resultAny.vsGamesCompletedComparison.currentPeriodTotal ?? 0,
            previousPeriodTotal: resultAny.vsGamesCompletedComparison.previousPeriodTotal ?? 0,
            percentageChange: resultAny.vsGamesCompletedComparison.percentageChange ?? 0,
          } : undefined;

          const singleCompletedComparison = resultAny?.singleGamesCompletedComparison ? {
            currentPeriodTotal: resultAny.singleGamesCompletedComparison.currentPeriodTotal ?? 0,
            previousPeriodTotal: resultAny.singleGamesCompletedComparison.previousPeriodTotal ?? 0,
            percentageChange: resultAny.singleGamesCompletedComparison.percentageChange ?? 0,
          } : undefined;

          const vsBotCompleted = resultAny?.vsBotCompletedComparison ? {
            currentPeriodTotal: resultAny.vsBotCompletedComparison.currentPeriodTotal ?? 0,
            previousPeriodTotal: resultAny.vsBotCompletedComparison.previousPeriodTotal ?? 0,
            percentageChange: resultAny.vsBotCompletedComparison.percentageChange ?? 0,
          } : undefined;

          // Daily challenge participation (unique users)
          const participation = resultAny?.dailyChallengeParticipation ? {
            uniqueUsersAttempted: resultAny.dailyChallengeParticipation.uniqueUsersAttempted ?? 0,
            uniqueActiveUsers: resultAny.dailyChallengeParticipation.uniqueActiveUsers ?? 0,
            participationRate: resultAny.dailyChallengeParticipation.participationRate ?? 0,
            previousUniqueUsersAttempted: resultAny.dailyChallengeParticipation.previousUniqueUsersAttempted ?? 0,
            previousUniqueActiveUsers: resultAny.dailyChallengeParticipation.previousUniqueActiveUsers ?? 0,
            previousParticipationRate: resultAny.dailyChallengeParticipation.previousParticipationRate ?? 0,
            participationRateChange: resultAny.dailyChallengeParticipation.participationRateChange ?? 0,
          } : undefined;

          // Update cache
          globalCache[cacheKey] = {
            data: mappedData,
            totalGames: total,
            vsGamesComparison: vsComparison,
            singleGamesComparison: singleComparison,
            dailyChallengeComparison: dailyComparison,
            vsBotComparison: vsBotComp,
            vsGamesCompletedComparison: vsCompletedComparison,
            singleGamesCompletedComparison: singleCompletedComparison,
            vsBotCompletedComparison: vsBotCompleted,
            dailyChallengeParticipation: participation,
          };

          setData(mappedData);
          setTotalGames(total);
          setVsGamesComparison(vsComparison);
          setSingleGamesComparison(singleComparison);
          setDailyChallengeComparison(dailyComparison);
          setVsBotComparison(vsBotComp);
          setVsGamesCompletedComparison(vsCompletedComparison);
          setSingleGamesCompletedComparison(singleCompletedComparison);
          setVsBotCompletedComparison(vsBotCompleted);
          setDailyChallengeParticipation(participation);
        }
      } catch (err: any) {
        if (isMounted && !isRequestCancelled(err)) {
          console.error('Failed to fetch game history:', err);
          setError(err.message || 'Failed to load chart data');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      source.cancel('Period changed or component unmounted');
    };
  }, [cacheKey, period, customRange]);

  return {
    data,
    totalGames,
    loading,
    error,
    // Started games comparisons
    vsGamesComparison,
    singleGamesComparison,
    dailyChallengeComparison,
    vsBotComparison,
    // Completed games comparisons
    vsGamesCompletedComparison,
    singleGamesCompletedComparison,
    vsBotCompletedComparison,
    // Daily challenge participation (unique users)
    dailyChallengeParticipation,
  };
};

// Utility to clear cache if needed (e.g., on refresh button)
export const clearGameHistoryCache = () => {
  Object.keys(globalCache).forEach((key) => delete globalCache[key]);
};
